import React, { useState, useEffect, useMemo } from 'react';
import { 
    CheckCircle2, 
    Clock, 
    Search, 
    Users, 
    AlertTriangle, 
    Loader2, 
    WifiOff, 
    Save, 
    Edit, 
    ShieldCheck, 
    KeyRound, 
    Sun, 
    Moon 
} from 'lucide-react';
import { toast } from 'sonner';
import useCalendarStore from '../store/useCalendarStore';

const API = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ─── Student Score Input Row ─────────────────────────────
const StudentScoreRow = React.memo(function StudentScoreRow({ student, score, totalMarks, isEditing, onChange }) {
    const isSet = score !== null && score !== undefined && score !== '';
    const percentage = isSet && totalMarks > 0 ? (parseFloat(score) / totalMarks) * 100 : 0;
    
    // Determine color based on performance percentage
    let scoreColor = 'text-zinc-400';
    let bgClass = 'bg-white border-zinc-100 dark:bg-zinc-900 dark:border-zinc-800';
    if (isSet) {
        if (percentage >= 75) {
            scoreColor = 'text-emerald-600 dark:text-emerald-400';
            bgClass = 'bg-emerald-50/50 border-emerald-100 dark:bg-emerald-950/20 dark:border-emerald-900/30';
        } else if (percentage >= 40) {
            scoreColor = 'text-primary';
            bgClass = 'bg-primary/5 border-primary/20 dark:bg-primary/10 dark:border-primary/30';
        } else {
            scoreColor = 'text-rose-600 dark:text-rose-400';
            bgClass = 'bg-rose-50/50 border-rose-100 dark:bg-rose-955/20 dark:border-rose-900/30';
        }
    }

    return (
        <div className={`flex items-center justify-between p-4 rounded-2xl border transition-all duration-200 ${bgClass}`}>
            <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shrink-0 ${isSet ? 'bg-zinc-100 dark:bg-zinc-800' : 'bg-zinc-50 dark:bg-zinc-800 text-zinc-450 dark:text-zinc-550'}`}>
                    {student.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                    <p className="font-bold text-sm text-zinc-800 dark:text-zinc-200 truncate">
                        {student.name}
                    </p>
                    <p className="text-[9px] text-zinc-400 dark:text-zinc-500 font-black uppercase tracking-widest mt-0.5">
                        {student.enrollment_number || 'STU-ID'}
                    </p>
                </div>
            </div>

            <div className="shrink-0 flex items-center gap-2">
                {isEditing ? (
                    <div className="flex items-center gap-1.5 bg-zinc-50 dark:bg-zinc-800 px-3 py-1.5 rounded-xl border border-zinc-200 dark:border-zinc-700">
                        <input
                            type="number"
                            min="0"
                            max={totalMarks}
                            step="any"
                            placeholder="-"
                            value={score ?? ''}
                            onChange={(e) => onChange(student.id, e.target.value)}
                            className="w-16 bg-transparent text-right font-black text-sm outline-none focus:ring-0 border-none p-0 text-zinc-800 dark:text-zinc-200"
                        />
                        <span className="text-xs text-zinc-450 font-bold">/ {totalMarks}</span>
                    </div>
                ) : (
                    <div className="text-right">
                        <p className={`font-black text-base ${scoreColor}`}>
                            {isSet ? score : 'Unmarked'}
                        </p>
                        {isSet && (
                            <p className="text-[9px] font-bold text-zinc-450 uppercase tracking-widest">
                                {totalMarks > 0 ? `${percentage.toFixed(0)}%` : ''}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});

// ─── Main Page ───────────────────────────────────────────
export default function PublicTestPage() {
    const darkMode = useCalendarStore(s => s.darkMode);
    const setDarkMode = useCalendarStore(s => s.setDarkMode);

    const [session, setSession] = useState(null);
    const [records, setRecords] = useState({});
    const [pendingChanges, setPendingChanges] = useState({}); // student_id -> score
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    
    // Auth Code States
    const [code, setCode] = useState('');
    const [authorizedCode, setAuthorizedCode] = useState('');
    const [authorizing, setAuthorizing] = useState(false);

    // Extract token from ?token=...
    const token = useMemo(() => {
        return new URLSearchParams(window.location.search).get('token');
    }, []);

    // Initial check: if we have token, but no code, we wait. If code enters, we fetch.
    const handleAuthorize = async (e) => {
        if (e) e.preventDefault();
        if (!code || code.length !== 4) {
            toast.error('Please enter a valid 4-digit code');
            return;
        }

        setAuthorizing(true);
        setError(null);
        try {
            const res = await fetch(`${API}/api/tests/public/session?token=${encodeURIComponent(token)}&code=${code}`);
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || 'Access denied');

            setSession(data);
            setRecords(data.scores || {});
            setAuthorizedCode(code);
            toast.success('Authorized successfully!');
        } catch (err) {
            setError(err.message);
        } finally {
            setAuthorizing(false);
        }
    };

    const handleScoreChange = React.useCallback((studentId, value) => {
        setPendingChanges(prev => {
            if (value === '') {
                return { ...prev, [studentId]: null };
            }
            const valNum = parseFloat(value);
            if (isNaN(valNum)) return prev;
            return { ...prev, [studentId]: valNum };
        });
    }, []);

    const handleSave = React.useCallback(async () => {
        if (saving || !session || !authorizedCode) return;
        
        setSaving(true);
        try {
            // Build scores payload matching TestBulkMarkRequest
            const scores = session.students.map(student => {
                const finalScore = pendingChanges[student.id] !== undefined
                    ? pendingChanges[student.id]
                    : (records[student.id] !== undefined ? records[student.id] : null);
                return {
                    student_id: student.id,
                    score: finalScore
                };
            });

            const res = await fetch(`${API}/api/tests/public/bulk-mark`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token,
                    code: authorizedCode,
                    scores
                }),
            });
            
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || 'Failed to save test scores');

            // Merge pendingChanges into records
            setRecords(prev => {
                const newRecords = { ...prev };
                session.students.forEach(student => {
                    const val = pendingChanges[student.id];
                    if (val === null) {
                        delete newRecords[student.id];
                    } else if (val !== undefined) {
                        newRecords[student.id] = val;
                    }
                });
                return newRecords;
            });
            setPendingChanges({});
            setIsEditing(false);
            toast.success('Test scores saved successfully!');
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    }, [pendingChanges, records, session, saving, token, authorizedCode]);

    const filteredStudents = useMemo(() => {
        if (!session) return [];
        return session.students.filter(s => {
            const matchesSearch = s.name.toLowerCase().includes(search.toLowerCase()) ||
                                (s.enrollment_number || '').toLowerCase().includes(search.toLowerCase());
            return matchesSearch;
        });
    }, [session, search]);

    const stats = useMemo(() => {
        if (!session) return { marked: 0, total: 0, classAvg: 0 };
        const total = session.students.length;
        let marked = 0;
        let sum = 0;
        
        session.students.forEach(s => {
            const val = pendingChanges[s.id] !== undefined ? pendingChanges[s.id] : records[s.id];
            if (val !== null && val !== undefined && val !== '') {
                marked++;
                sum += parseFloat(val);
            }
        });

        const classAvg = marked > 0 ? (sum / (marked * session.total_marks)) * 100 : 0;
        return { marked, total, classAvg };
    }, [records, pendingChanges, session]);

    // ─── Step 1: Verification / Code Entry Page ────────────
    if (!token) {
        return (
            <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex flex-col items-center justify-center p-8 text-center">
                <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-6 rotate-12 shadow-2xl bg-rose-100 dark:bg-rose-955/20 text-rose-600">
                    <AlertTriangle className="w-10 h-10" />
                </div>
                <h1 className="text-2xl font-black text-zinc-900 dark:text-zinc-50 mb-2">Invalid Session</h1>
                <p className="text-zinc-500 dark:text-zinc-400 text-sm leading-relaxed mb-8">No session token was provided in the link query parameters.</p>
            </div>
        );
    }

    if (!authorizedCode) {
        return (
            <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center p-4 relative overflow-hidden">
                <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-rose-500/5 rounded-full blur-3xl pointer-events-none" />

                <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl w-full max-w-md rounded-3xl shadow-xl border border-zinc-200/50 dark:border-zinc-800/85 overflow-hidden p-6 sm:p-8 space-y-6 relative z-10">
                    <div className="flex justify-end">
                        <button 
                            onClick={() => setDarkMode(!darkMode)}
                            className="p-2 rounded-xl bg-zinc-150/50 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                        >
                            {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </button>
                    </div>

                    <div className="text-center space-y-2">
                        <div className="mx-auto w-12 h-12 bg-primary/10 text-primary rounded-2xl flex items-center justify-center">
                            <KeyRound className="w-6 h-6" />
                        </div>
                        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Access Authorization</h2>
                        <p className="text-xs text-zinc-400 dark:text-zinc-500 leading-relaxed">
                            This test scores link is protected. Enter the 4-digit code to view or edit the scores.
                        </p>
                    </div>

                    <form onSubmit={handleAuthorize} className="space-y-4">
                        <div>
                            <input
                                type="text"
                                maxLength={4}
                                pattern="\d{4}"
                                inputMode="numeric"
                                placeholder="• • • •"
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                className="w-full text-center text-2xl font-bold tracking-[0.5em] py-3.5 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700 rounded-2xl outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-300 dark:placeholder:text-zinc-600 placeholder:tracking-normal font-mono"
                                required
                            />
                        </div>

                        {error && (
                            <div className="p-3 bg-rose-50 dark:bg-rose-955/20 text-rose-600 dark:text-rose-450 text-xs font-semibold rounded-xl text-center">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={authorizing}
                            className="w-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold py-3.5 rounded-2xl shadow-lg shadow-primary/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                        >
                            {authorizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                            Verify Access Code
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // ─── Step 2: Scoring Portal View ───────────────────────
    return (
        <div className="min-h-screen bg-slate-50 dark:bg-zinc-950 pb-32">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-white/95 dark:bg-zinc-900/90 backdrop-blur-xl border-b border-zinc-200/50 dark:border-zinc-800/80">
                <div className="max-w-lg mx-auto px-4 sm:px-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                            <div className="flex items-center gap-1.5 mb-1">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                <p className="text-[10px] text-zinc-400 dark:text-zinc-550 font-black uppercase tracking-widest leading-none">Test Portal</p>
                            </div>
                            <h1 className="font-black text-base sm:text-xl text-zinc-900 dark:text-zinc-550 leading-tight truncate">
                                {session.name}
                            </h1>
                            <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1 font-semibold truncate">
                                {[session.batch_name, session.subject_name].filter(Boolean).join(' • ')} (Max: {session.total_marks}m)
                            </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                            {/* Theme Toggle */}
                            <button 
                                onClick={() => setDarkMode(!darkMode)}
                                className="p-2 rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-750 transition-colors"
                            >
                                {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                            </button>

                            <div className="text-right">
                                <p className="text-xl sm:text-2xl font-black text-primary leading-none tracking-tighter">
                                    {stats.classAvg.toFixed(0)}%
                                </p>
                                <p className="text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest leading-none mt-1">Class Avg</p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 h-1 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden flex">
                        <div
                            className="h-full bg-emerald-500 rounded-full transition-all duration-700 ease-out"
                            style={{ width: stats.total > 0 ? `${(stats.marked / stats.total) * 100}%` : '0%' }}
                        />
                    </div>
                    <p className="text-[9.5px] text-zinc-400 dark:text-zinc-500 font-semibold mt-1.5 text-right uppercase tracking-wider">
                        {stats.marked} of {stats.total} Marked
                    </p>
                </div>
            </header>

            <div className="max-w-lg mx-auto px-4 sm:px-6 mt-6 space-y-6">
                {/* Search */}
                <div className="relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 transition-colors" />
                    <input
                        type="text"
                        placeholder="Search student..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-11 pr-4 py-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 focus:border-primary/30 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-primary/20 outline-none text-zinc-800 dark:text-zinc-100 transition-all shadow-sm"
                    />
                </div>

                {/* Student List */}
                <div className="grid gap-3">
                    {filteredStudents.length === 0 ? (
                        <div className="py-20 text-center border-2 border-dashed border-zinc-200 dark:border-zinc-800 rounded-3xl">
                            <Users className="w-12 h-12 text-zinc-350 dark:text-zinc-750 mx-auto mb-4" />
                            <p className="text-zinc-450 dark:text-zinc-550 font-bold text-xs uppercase tracking-widest">No students found</p>
                        </div>
                    ) : (
                        filteredStudents.map(student => {
                            const score = pendingChanges[student.id] !== undefined
                                ? pendingChanges[student.id]
                                : (records[student.id] !== undefined ? records[student.id] : null);
                            return (
                                <StudentScoreRow
                                    key={student.id}
                                    student={student}
                                    score={score}
                                    totalMarks={session.total_marks}
                                    isEditing={isEditing}
                                    onChange={handleScoreChange}
                                />
                            );
                        })
                    )}
                </div>
            </div>

            {/* Bottom Actions Bar */}
            <div className="fixed bottom-6 left-4 right-4 md:bottom-8 md:left-6 md:right-6 z-40 animate-in fade-in slide-in-from-bottom-8 duration-500">
                {!isEditing ? (
                    <div className="max-w-md mx-auto bg-zinc-900 dark:bg-zinc-950 rounded-3xl shadow-2xl p-2 flex items-center justify-between">
                        <div className="px-4 sm:px-6">
                            <p className="text-white text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] opacity-50">Portal Mode</p>
                            <p className="text-white text-sm sm:text-base font-bold leading-none mt-0.5">Read Only</p>
                        </div>
                        <button 
                            onClick={() => setIsEditing(true)}
                            className="flex items-center gap-2 bg-primary hover:bg-primary/95 text-primary-foreground px-5 sm:px-8 py-3.5 sm:py-4 rounded-2xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all"
                        >
                            <Edit className="w-4 h-4" />
                            Edit Scores
                        </button>
                    </div>
                ) : (
                    <div className="max-w-md mx-auto bg-zinc-900 dark:bg-zinc-950 rounded-3xl shadow-2xl p-2 flex items-center gap-2">
                        <button 
                            onClick={() => {
                                setPendingChanges({});
                                setIsEditing(false);
                            }}
                            className="flex-1 text-white hover:text-zinc-300 px-3 py-3.5 sm:py-4 rounded-2xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all text-center"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleSave}
                            disabled={saving}
                            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-5 sm:px-8 py-3.5 sm:py-4 rounded-2xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all disabled:opacity-50"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                )}
            </div>

            <footer className="max-w-lg mx-auto px-6 py-12 text-center opacity-30 select-none">
                <WifiOff className="w-4 h-4 mx-auto mb-2 dark:text-zinc-400" />
                <p className="text-[10px] font-bold uppercase tracking-widest font-mono dark:text-zinc-400">Authorized Session (Temporary)</p>
            </footer>
        </div>
    );
}
